import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { LEGAL_DOCS } from "@/data/legal";
import { SUPPORT_EMAIL } from "@/lib/site";
import { setSeo } from "@/lib/seo";

const META_DESCRIPTIONS = {
  privacy:
    "Privacy & Cookie Policy for Blue Shuck Consulting LTD: how we collect, use and protect your personal data in line with UK GDPR and the Data Protection Act 2018.",
  terms:
    "Terms of Service for Blue Shuck Consulting LTD software, covering access, subscriptions, data ownership, liability and governing law (England and Wales).",
  accessibility:
    "Accessibility Statement for Blue Shuck Consulting LTD: our commitment to WCAG 2.2 Level AA, accessibility features, known limitations and how to give feedback.",
};

const renderText = (text) => {
  const parts = text.split(SUPPORT_EMAIL);
  if (parts.length === 1) return text;
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="text-brand-blue font-medium hover:underline"
        >
          {SUPPORT_EMAIL}
        </a>
      )}
    </span>
  ));
};

export default function LegalPage({ slug }) {
  const doc = LEGAL_DOCS[slug];

  useEffect(() => {
    if (doc) {
      setSeo({
        title: `${doc.title} | Blue Shuck Consulting LTD`,
        description: META_DESCRIPTIONS[slug],
      });
    }
    window.scrollTo(0, 0);
  }, [doc, slug]);

  if (!doc) return null;

  return (
    <div className="App bg-white min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-32 pb-24">
        <article className="mx-auto max-w-3xl px-6 md:px-10">
          <Link
            data-testid="legal-back-home"
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-brand-navy transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>

          <h1 className="mt-6 font-heading font-800 text-4xl sm:text-5xl tracking-tight text-brand-navy">
            {doc.title}
          </h1>
          <p className="mt-3 text-sm text-slate-500">
            Last updated: {doc.lastUpdated}
          </p>

          {doc.intro && (
            <p className="mt-8 text-lg leading-relaxed text-slate-600">
              {renderText(doc.intro)}
            </p>
          )}

          <div className="mt-10 space-y-10">
            {doc.sections.map((section, i) => (
              <section key={i} data-testid={`legal-section-${i}`}>
                <h2 className="font-heading font-700 text-xl sm:text-2xl tracking-tight text-brand-navy">
                  {section.heading}
                </h2>
                {section.body &&
                  section.body.map((p, j) => (
                    <p
                      key={j}
                      className="mt-4 text-base leading-relaxed text-slate-600"
                    >
                      {renderText(p)}
                    </p>
                  ))}
                {section.bullets && (
                  <ul className="mt-4 space-y-3">
                    {section.bullets.map((b, k) => (
                      <li
                        key={k}
                        className="flex gap-3 text-base leading-relaxed text-slate-600"
                      >
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-blue" />
                        <span>{renderText(b)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {section.after &&
                  section.after.map((p, m) => (
                    <p
                      key={m}
                      className="mt-4 text-base leading-relaxed text-slate-600"
                    >
                      {renderText(p)}
                    </p>
                  ))}
              </section>
            ))}
          </div>

          <div className="mt-14 rounded-2xl border border-slate-200 bg-slate-50 p-8">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <p className="font-heading font-600 text-brand-navy">
                  Questions about this policy?
                </p>
                <a
                  data-testid="legal-contact-email"
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-sm text-brand-blue hover:underline"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
